export const TRADE_COMMENT_TEMPLATE = [
  "HTF context:",
  "Macro / BTC / market regime:",
  "Liquidity sweep or recent grabs:",
  "HTF reversal / invalidation zone:",
  "Structure: trend, CHoCH/BOS, compression:",
  "Trigger:",
  "Confirmation:",
  "Invalidation:",
  "TP logic / RR:",
  "Risk notes:",
  "Execution notes:",
  "Post-trade review:"
].join("\n");

export const activeTradeStatuses = new Set(["SUBMITTED", "WAITING_ENTRY", "PARTIAL_ENTRY", "OPEN", "PARTIALLY_CLOSED"]);

export function isActiveTrade(trade) {
  return activeTradeStatuses.has(trade?.status);
}

export function tradePnl(trade) {
  if (trade?.realizedPnlWithFees != null) return Number(trade.realizedPnlWithFees);
  if (trade?.realizedPnlRaw != null) return Number(trade.realizedPnlRaw);
  return null;
}

export function remainingTradeQuantity(trade) {
  const initial = Number(trade?.initialQuantity);
  if (!Number.isFinite(initial) || initial <= 0) return null;

  const closed = [trade?.tp1Quantity, trade?.tp2Quantity]
    .map(Number)
    .filter(Number.isFinite)
    .reduce((sum, quantity) => sum + quantity, 0);

  return Math.max(0, initial - closed);
}

export function parseTradeNumber(value) {
  if (value == null) return null;
  const parsed = Number(String(value).replace(",", ".").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function tradeAmountText(trade) {
  const margin = parseTradeNumber(trade?.value);
  const leverage = parseTradeNumber(trade?.leverage);
  if (margin != null && margin > 0 && leverage != null && leverage > 0) {
    return `${margin.toFixed(margin % 1 === 0 ? 0 : 2)} USDT x${leverage.toFixed(leverage % 1 === 0 ? 0 : 1)}`;
  }

  const entry = Number(trade?.actualEntry || trade?.plannedEntry);
  const quantity = remainingTradeQuantity(trade) ?? Number(trade?.initialQuantity);
  if (Number.isFinite(entry) && Number.isFinite(quantity) && entry > 0 && quantity > 0) {
    return `${Math.abs(entry * quantity).toFixed(2)} USDT`;
  }
  return null;
}

export function potentialStopLossAmount(trade) {
  const entry = Number(trade?.actualEntry || trade?.plannedEntry);
  const stopLoss = Number(trade?.stopLoss);
  if (!Number.isFinite(entry) || !Number.isFinite(stopLoss) || entry <= 0) {
    return null;
  }

  const remainingQuantity = remainingTradeQuantity(trade);
  if (remainingQuantity != null && remainingQuantity > 0) {
    const risk = Math.abs((stopLoss - entry) * remainingQuantity);
    return Number.isFinite(risk) ? risk : null;
  }

  const margin = parseTradeNumber(trade?.value);
  const leverage = parseTradeNumber(trade?.leverage);
  if (margin != null && margin > 0 && leverage != null && leverage > 0) {
    const quantity = (margin * leverage) / entry;
    const risk = Math.abs((stopLoss - entry) * quantity);
    return Number.isFinite(risk) ? risk : null;
  }
  return null;
}

export function activeTradePnl(trade, currentPrice) {
  if (!isActiveTrade(trade)) return tradePnl(trade);

  const entry = Number(trade?.actualEntry);
  const price = Number(currentPrice);
  const remainingQuantity = remainingTradeQuantity(trade);
  const realized = tradePnl(trade) || 0;

  if (!Number.isFinite(entry) || !Number.isFinite(price) || remainingQuantity == null) {
    return tradePnl(trade);
  }

  const unrealized = trade?.side === "SHORT"
    ? (entry - price) * remainingQuantity
    : (price - entry) * remainingQuantity;

  return realized + unrealized;
}

export function formatTradePrice(price) {
  if (price == null || Number.isNaN(Number(price))) return "HOLD";
  const n = Number(price);
  if (n >= 100) return n.toFixed(2);
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(7);
}

export function tradeEntryLabel(trade, exchangeEntryPrice) {
  const actualEntry = exchangeEntryPrice ?? trade?.actualEntry;
  if (actualEntry != null) return `Entry ${formatTradePrice(actualEntry)}`;

  if (trade?.entryPriceExpression) {
    if (trade?.frozenEntryPrice != null) {
      return `Entry frozen ${formatTradePrice(trade.frozenEntryPrice)}`;
    }
    const resolved = trade?.lastResolvedEntryPrice ?? trade?.plannedEntry;
    return `Entry ${trade.entryPriceExpression} -> ${formatTradePrice(resolved)}`;
  }
  return `Entry ${formatTradePrice(trade?.plannedEntry)}`;
}

export function formatPnl(value) {
  if (value == null || Number.isNaN(Number(value))) return "";
  const n = Number(value);
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)} USDT`;
}

export function encodeCommandText(value) {
  return String(value || "").trim().replace(/\s+/g, "%20");
}

export const TRADE_ENTRY_SOURCES = {
  ABSOLUTE_PRICE: "ABSOLUTE_PRICE",
  SELECTED_CANDLE: "SELECTED_CANDLE",
  DYNAMIC: "DYNAMIC"
};

export const TRADE_DYNAMIC_ENTRY_OPTIONS = [
  "*BB 15M", "*BB 4H", "*BB 1D",
  "*15M-MA", "*4H-MA", "*1D-MA"
];

export const TRADE_CANDLE_ENTRY_OPTIONS = {
  LONG: [
    "OPEN", "CLOSE", "MARKET",
    "BODY 10%", "BODY 50%", "BODY 90%",
    "LOW", "LOW -0.5%", "LOW -1%", "LOW -5%",
    "WICK 10%", "WICK 25%", "WICK 50%", "WICK 75%", "WICK 90%"
  ],
  SHORT: [
    "OPEN", "CLOSE", "MARKET",
    "BODY 10%", "BODY 50%", "BODY 90%",
    "HIGH", "HIGH +0.5%", "HIGH +1%", "HIGH +5%",
    "WICK 10%", "WICK 25%", "WICK 50%", "WICK 75%", "WICK 90%"
  ]
};

export const TRADE_STOP_LOSS_OPTIONS = {
  LONG: [
    "NONE", "-0.5%", "-1%", "-1.5%", "-2%", "-5%", "-10%", "-20%", "-50%",
    "LOW -0.1%", "LOW -0.5%", "LOW -1%", "LOW -2%", "LOW -5%", "LOW -10%",
    "Current 15M Low", "Previous 15M Low", "Current 4H Low", "Previous 4H Low",
    "Current 1D Low", "Previous 1D Low"
  ],
  SHORT: [
    "NONE", "0.5%", "1%", "1.5%", "2%", "5%", "10%", "20%", "50%",
    "HIGH +0.1%", "HIGH +0.5%", "HIGH +1%", "HIGH +2%", "HIGH +5%", "HIGH +10%",
    "Current 15M High", "Previous 15M High", "Current 4H High", "Previous 4H High",
    "Current 1D High", "Previous 1D High"
  ]
};

export function hasSelectedCandleContext(candle) {
  if (!candle) return false;
  return [candle.time, candle.open, candle.high, candle.low, candle.close]
    .every((value) => Number.isFinite(Number(value)));
}

export function isSelectedCandlePriceSelector(value) {
  return /^(OPEN|CLOSE|BODY(?:\s+\d+(?:\.\d+)?%)?|WICK(?:\s+\d+(?:\.\d+)?%)?|LOW(?:\s+[+-]\d+(?:\.\d+)?%)?|HIGH(?:\s+[+-]\d+(?:\.\d+)?%)?)$/i
    .test(String(value || "").trim());
}

export function formatTradeContextLabel({ currency, interval, candleLabel, selectedPrice }) {
  const market = String(currency || "").toUpperCase();
  const timeframe = String(interval || "").replace(/^_/, "").toUpperCase();
  const context = candleLabel ? `${market}, ${timeframe} [${candleLabel}]` : `${market}, ${timeframe}`;
  const price = parseTradeNumber(selectedPrice);
  return price == null ? context : `${context} · ${price}`;
}

export const ENTRY_PROTECTION_MODES = ["OFF", "5M", "15M", "4H", "ADAPT15M"];

export function normalizeEntryProtectionMode(mode, legacyProtectEntry = false) {
  if (typeof mode === "boolean") return mode ? "5M" : "OFF";
  const normalized = String(mode || "").trim().toUpperCase().replace(/_/g, "");
  if (["YES", "TRUE", "ON"].includes(normalized)) return "5M";
  if (["NO", "FALSE", "NONE", "OFF"].includes(normalized)) return "OFF";
  if (ENTRY_PROTECTION_MODES.includes(normalized)) return normalized;
  return legacyProtectEntry ? "5M" : "OFF";
}

export function buildUnifiedTradeCommand({
  currency,
  side,
  amountUsdt,
  leverage,
  interval,
  entry,
  stopLoss,
  tp1,
  tp2,
  expiresIn,
  entryProtectionMode,
  protectEntry = false,
  comment,
  selectedCandle
}) {
  const normalizedCurrency = currencyFromFuturesSymbol(currency);
  const normalizedSide = String(side || "").toUpperCase();
  const normalizedLeverage = normalizeLeverage(leverage);
  const normalizedInterval = String(interval || "15M").replace(/^_/, "").toUpperCase();
  const entrySource = String(entry?.source || "").toUpperCase();
  const entryValue = String(entry?.value ?? "").trim();
  if (!normalizedCurrency || !normalizedSide || !amountUsdt || !normalizedLeverage || !entryValue) {
    return null;
  }

  const usesSelectedCandle = entrySource === TRADE_ENTRY_SOURCES.SELECTED_CANDLE
    || isSelectedCandlePriceSelector(stopLoss);
  if (usesSelectedCandle && !hasSelectedCandleContext(selectedCandle)) return null;

  const candleStart = usesSelectedCandle ? Math.trunc(Number(selectedCandle.time)) : "Previous";
  const protectionMode = normalizeEntryProtectionMode(entryProtectionMode, protectEntry);
  return `candleTrade ts ${normalizedSide} c ${normalizedCurrency} v ${amountUsdt} l ${normalizedLeverage}`
    + ` eci ${normalizedInterval} bcst ${candleStart} et ${encodeCommandText(entryValue)}`
    + ` slt ${encodeCommandText(stopLoss || "NONE")}`
    + ` expl ${encodeCommandText(tp1 || "NONE")}`
    + ` expl2 ${encodeCommandText(tp2 || "NONE")}`
    + ` ex ${expiresIn || "NONE"} epp ${protectionMode}`
    + ` cm ${comment ? encodeCommandText(comment) : "NONE"} confirm YES`;
}

export function priceProtectLabel(trade) {
  const mode = normalizeEntryProtectionMode(trade?.entryProtectionMode, trade?.entryPriceProtect);
  return `Protect ${mode}`;
}

export function tpLabel(trade) {
  const hasTp1 = trade?.tp1 != null;
  const hasTp2 = trade?.tp2 != null;
  const tp1Hold = Boolean(trade?.tp1Hold);
  const tp2Hold = Boolean(trade?.tp2Hold);

  if (!hasTp1 && !hasTp2) return "TP: HOLD";
  if (hasTp1 && !hasTp2 && !tp2Hold) return `TP: ${formatTradePrice(trade.tp1)}`;
  if (hasTp1 && (hasTp2 || tp2Hold)) {
    return `TP1: ${formatTradePrice(trade.tp1)}, TP2: ${tp2Hold ? "HOLD" : formatTradePrice(trade.tp2)}`;
  }
  if (tp1Hold) return "TP: HOLD";
  return "TP: HOLD";
}

export function closePositionCommand(trade) {
  if (!trade?.symbol || !trade?.side) return null;
  return buildPositionCloseCommand({
    symbol: trade.symbol,
    side: trade.side,
    amountPercent: 100
  });
}

export function currencyFromFuturesSymbol(symbol) {
  return String(symbol || "").toUpperCase().replace(/USDT$/, "");
}

export function normalizePercent(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  return raw.endsWith("%") ? raw : `${raw}%`;
}

export function normalizeLeverage(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim().toLowerCase();
  return raw.endsWith("x") ? raw : `${raw}x`;
}

export const LIQUIDITY_CATCH_TIMEFRAMES = ["15M", "4H", "1D"];
export const LIQUIDITY_CATCH_EXPIRIES = [6, 12, 24, 48, 72];
export const LIQUIDITY_CATCH_CANDLE_LEVELS = {
  LONG: [
    "OPEN", "CLOSE", "BODY 10%", "BODY 50%", "BODY 90%",
    "LOW", "LOW -0.5%", "LOW -1%", "LOW -5%",
    "WICK 10%", "WICK 25%", "WICK 50%", "WICK 75%", "WICK 90%"
  ],
  SHORT: [
    "OPEN", "CLOSE", "BODY 10%", "BODY 50%", "BODY 90%",
    "HIGH", "HIGH +0.5%", "HIGH +1%", "HIGH +5%",
    "WICK 10%", "WICK 25%", "WICK 50%", "WICK 75%", "WICK 90%"
  ]
};

export function normalizeLiquidityCatchTimeframe(timeframe) {
  const normalized = String(timeframe || "").replace(/^_/, "").toUpperCase();
  return LIQUIDITY_CATCH_TIMEFRAMES.includes(normalized) ? normalized : "15M";
}

export function resolveLiquidityCatchCandleLevel(candle, selector, side) {
  const open = Number(candle?.open);
  const high = Number(candle?.high);
  const low = Number(candle?.low);
  const close = Number(candle?.close);
  if (![open, high, low, close].every(Number.isFinite)) return null;

  const normalized = String(selector || "").trim().toUpperCase();
  const bodyLow = Math.min(open, close);
  const bodyHigh = Math.max(open, close);
  if (normalized === "OPEN") return open;
  if (normalized === "CLOSE") return close;

  const bodyMatch = normalized.match(/^BODY\s+(\d+(?:\.\d+)?)%$/);
  if (bodyMatch) {
    return bodyLow + ((bodyHigh - bodyLow) * Number(bodyMatch[1])) / 100;
  }

  const wickMatch = normalized.match(/^WICK\s+(\d+(?:\.\d+)?)%$/);
  if (wickMatch) {
    const fraction = Number(wickMatch[1]) / 100;
    return String(side || "").toUpperCase() === "SHORT"
      ? bodyHigh + (high - bodyHigh) * fraction
      : bodyLow - (bodyLow - low) * fraction;
  }

  const lowMatch = normalized.match(/^LOW(?:\s+([+-]\d+(?:\.\d+)?)%)?$/);
  if (lowMatch) return low * (1 + Number(lowMatch[1] || 0) / 100);
  const highMatch = normalized.match(/^HIGH(?:\s+([+-]\d+(?:\.\d+)?)%)?$/);
  if (highMatch) return high * (1 + Number(highMatch[1] || 0) / 100);
  return null;
}

export function liquidityCatchCandleTimestamp(candle, fallbackDate = null) {
  const candleTime = Number(candle?.time);
  if (Number.isFinite(candleTime) && candleTime > 0) return candleTime;
  const fallbackTime = new Date(fallbackDate || 0).getTime();
  return Number.isFinite(fallbackTime) && fallbackTime > 0 ? fallbackTime : null;
}

export function quickAddLabel(amountUsdt, leverage) {
  const normalizedLeverage = normalizeLeverage(leverage);
  return `${amountUsdt} x${String(normalizedLeverage || "").replace(/x$/i, "")}`;
}

function normalizedPositionSide(item) {
  const positionSide = String(item?.positionSide || "").toUpperCase();
  if (positionSide === "LONG" || positionSide === "SHORT") return positionSide;
  const amount = Number(item?.positionAmt);
  if (Number.isFinite(amount) && amount !== 0) return amount > 0 ? "LONG" : "SHORT";
  return String(item?.side || "").toUpperCase() === "SELL" ? "SHORT" : "LONG";
}

function isActivePositionOrder(order) {
  const status = String(order?.status || "").toUpperCase();
  return !["CANCELED", "CANCELLED", "EXPIRED", "FILLED", "REJECTED"].includes(status);
}

function isOpeningPositionOrder(order, positionSide) {
  if (order?.reduceOnly || order?.closePosition) return false;
  const side = String(order?.side || "").toUpperCase();
  return (positionSide === "LONG" && side === "BUY")
    || (positionSide === "SHORT" && side === "SELL");
}

function orderPositionSide(order) {
  const explicitSide = String(order?.positionSide || "").toUpperCase();
  if (explicitSide === "LONG" || explicitSide === "SHORT") return explicitSide;
  const side = String(order?.side || "").toUpperCase();
  if (order?.reduceOnly || order?.closePosition) {
    return side === "BUY" ? "SHORT" : "LONG";
  }
  return side === "SELL" ? "SHORT" : "LONG";
}

export function buildLivePositionLanes(positionJournal, symbol) {
  if (positionJournal?.status && positionJournal.status !== "CURRENT") return [];
  const normalizedSymbol = String(symbol || "").toUpperCase();
  const activeOrders = (positionJournal?.orders || []).filter((order) => (
    (!normalizedSymbol || String(order?.symbol || "").toUpperCase() === normalizedSymbol)
      && isActivePositionOrder(order)
  ));
  const result = (positionJournal?.lanes || [])
    .filter((lane) => !normalizedSymbol || String(lane?.symbol || "").toUpperCase() === normalizedSymbol)
    .map((lane) => {
      const positionSide = normalizedPositionSide(lane);
      return {
        key: `${String(lane.symbol || normalizedSymbol).toUpperCase()}|${positionSide}`,
        symbol: String(lane.symbol || normalizedSymbol).toUpperCase(),
        positionSide,
        pending: false,
        lane,
        orders: activeOrders.filter((order) => orderPositionSide(order) === positionSide)
      };
    });

  activeOrders.forEach((order) => {
    const positionSide = orderPositionSide(order);
    const orderSymbol = String(order?.symbol || normalizedSymbol).toUpperCase();
    if (!isOpeningPositionOrder(order, positionSide)) return;
    if (result.some((item) => item.symbol === orderSymbol && item.positionSide === positionSide)) return;
    result.push({
      key: `${orderSymbol}|${positionSide}`,
      symbol: orderSymbol,
      positionSide,
      pending: true,
      lane: null,
      orders: activeOrders.filter((item) => (
        String(item?.symbol || "").toUpperCase() === orderSymbol
          && orderPositionSide(item) === positionSide
      ))
    });
  });

  return result.sort((a, b) => {
    if (a.symbol !== b.symbol) return a.symbol.localeCompare(b.symbol);
    return a.positionSide === "LONG" ? -1 : 1;
  });
}

export function buildOpenPositionCommand({ symbol, side, amountUsdt, leverage, price }) {
  const currency = currencyFromFuturesSymbol(symbol);
  const normalizedSide = String(side || "").toUpperCase();
  const normalizedLeverage = normalizeLeverage(leverage);
  if (!currency || !normalizedSide || !amountUsdt || !normalizedLeverage) return null;
  const priceText = price == null || price === "" ? "Market" : String(price);
  return `openPos ts ${normalizedSide} c ${currency} v ${amountUsdt} l ${normalizedLeverage} p ${priceText} confirm YES`;
}

export function buildPositionCloseCommand({ symbol, side, amountPercent, price = "market" }) {
  const normalizedSymbol = String(symbol || "").toUpperCase();
  const normalizedSide = String(side || "").toUpperCase();
  const amount = normalizePercent(amountPercent);
  if (!normalizedSymbol || !normalizedSide || !amount) return null;
  return `closePosition p ${normalizedSymbol}|${normalizedSide} cp ${price} a ${amount} sw No confirm YES`;
}

export function buildTpSlCommand({ symbol, side, type, amountPercent, price }) {
  const currency = currencyFromFuturesSymbol(symbol);
  const normalizedSide = String(side || "").toUpperCase();
  const amount = normalizePercent(amountPercent);
  if (!currency || !normalizedSide || !type || !amount || price == null || price === "") return null;
  return `sltp ts ${normalizedSide} type ${type} c ${currency} a ${amount} p ${price} confirm YES`;
}

export function buildLiquidityCatchCommand({
  symbol,
  side,
  amountUsdt,
  leverage,
  timeframe,
  baseline,
  stopLossPercent,
  tp1Percent,
  tp2Percent,
  expiresIn,
  entryProtectionMode,
  protectEntry = false,
  baselineSource = "MANUAL",
  baselineSelector = "PRICE",
  baselineCandleTimestamp = null
}) {
  const currency = currencyFromFuturesSymbol(symbol);
  const normalizedSide = String(side || "").toUpperCase();
  const normalizedLeverage = normalizeLeverage(leverage);
  const normalizedTimeframe = String(timeframe || "").replace(/^_/, "").toUpperCase();
  const tp2 = tp2Percent == null || String(tp2Percent).trim() === ""
    ? "HOLD"
    : String(tp2Percent).toUpperCase();
  if (!currency || !normalizedSide || !amountUsdt || !normalizedLeverage
      || !["15M", "4H", "1D"].includes(normalizedTimeframe)
      || !baseline || !stopLossPercent || !tp1Percent || !expiresIn) {
    return null;
  }
  const source = String(baselineSource || "MANUAL").toUpperCase();
  const selector = encodeCommandText(baselineSelector || "PRICE");
  const candleTimestamp = Number(baselineCandleTimestamp);
  const protectionMode = normalizeEntryProtectionMode(entryProtectionMode, protectEntry);
  return `catchTrade ts ${normalizedSide} c ${currency} v ${amountUsdt} l ${normalizedLeverage}`
    + ` tf ${normalizedTimeframe} b ${baseline} sl ${stopLossPercent} tp1 ${tp1Percent}`
    + ` tp2 ${tp2} ex ${expiresIn} epp ${protectionMode}`
    + ` bs ${source} bsel ${selector}`
    + ` bcts ${Number.isFinite(candleTimestamp) && candleTimestamp > 0 ? Math.trunc(candleTimestamp) : 0}`
    + ` confirm YES`;
}

function formatPercentValue(value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const rounded = Number(value).toFixed(2).replace(/\.?0+$/, "");
  return `${rounded}%`;
}

function priceTargetToPercent(trade, target) {
  const entry = Number(trade?.actualEntry || trade?.plannedEntry);
  const price = Number(target);
  if (!Number.isFinite(entry) || !Number.isFinite(price) || entry <= 0) {
    return null;
  }

  const pct = trade?.side === "SHORT"
    ? ((entry - price) / entry) * 100
    : ((price - entry) / entry) * 100;

  return pct > 0 ? formatPercentValue(pct) : null;
}

export function buildReopenTradeData(trade, currentPrice) {
  const volume = parseTradeNumber(trade?.value);
  const price = Number(currentPrice ?? trade?.actualClose ?? trade?.actualEntry ?? trade?.plannedEntry);
  const noTp = trade?.tp1 == null && trade?.tp2 == null && !trade?.tp1Hold && !trade?.tp2Hold;
  const data = {
    currency: trade?.cur,
    tradeSide: trade?.side,
    interval: "15M",
    candle: "15M",
    volume: volume != null && volume > 0 ? String(volume) : undefined,
    leverage: trade?.leverage != null ? String(trade.leverage) : undefined,
    price: Number.isFinite(price) ? price : undefined,
    stopLoss: trade?.stopLoss != null ? String(trade.stopLoss) : undefined,
    takeProfit: trade?.tp1Hold || noTp ? "HOLD" : priceTargetToPercent(trade, trade?.tp1),
    takeProfit2: trade?.tp2Hold ? "HOLD" : priceTargetToPercent(trade, trade?.tp2),
    protectEntry: Boolean(trade?.entryPriceProtect),
    entryProtectionMode: normalizeEntryProtectionMode(trade?.entryProtectionMode, trade?.entryPriceProtect),
    comment: trade?.comment || ""
  };
  Object.keys(data).forEach((key) => data[key] === undefined && delete data[key]);
  return data;
}

export function buildTradeEvent(trade) {
  return {
    cur: trade.cur,
    name: `${trade.cur} ${trade.side} ${trade.status}`,
    tf: "_15M",
    ts: trade.submittedAt,
    tradeDrawId: trade.id,
    focusLatest: isActiveTrade(trade),
    trade
  };
}
